"use client";

import { useUserInfo } from "@/store/user-info";
import type { OrderType } from "../types";

const STORAGE_KEY_PREFIX = "tradePanelOrderType";

interface OrderTypeMemory {
    userId: number;
    orderType: OrderType;
}

function isOrderType( value: unknown ): value is OrderType {
    return value === "market" || value === "limit";
}

function getCurrentUserId() {
    const userId = Number( useUserInfo.getState().userId );
    return Number.isFinite( userId ) && userId > 0 ? userId : undefined;
}

function getStorageKey( userId: number ) {
    return `${STORAGE_KEY_PREFIX}:${userId}`;
}

function readOrderTypeMemory() {
    if ( typeof window === "undefined" ) return undefined;

    const userId = getCurrentUserId();
    if ( userId === undefined ) return undefined;

    try {
        const value = window.localStorage.getItem( getStorageKey( userId ) );
        return isOrderType( value ) ? value : undefined;
    } catch ( error ) {
        console.error( "Failed to restore trade panel order type:", error );
        return undefined;
    }
}

function writeOrderTypeMemory( orderType: OrderType ) {
    if ( typeof window === "undefined" ) return;

    const userId = getCurrentUserId();
    if ( userId === undefined ) return;

    try {
        window.localStorage.setItem( getStorageKey( userId ), orderType );
    } catch ( error ) {
        console.error( "Failed to save trade panel order type:", error );
    }
}

function removeOrderTypeMemory() {
    if ( typeof window === "undefined" ) return;

    const userId = getCurrentUserId();
    if ( userId === undefined ) return;

    try {
        window.localStorage.removeItem( getStorageKey( userId ) );
    } catch ( error ) {
        console.error( "Failed to clear trade panel order type:", error );
    }
}

function removeAllOrderTypeMemory() {
    if ( typeof window === "undefined" ) return;

    try {
        window.localStorage.removeItem( STORAGE_KEY_PREFIX );
        for ( let index = window.localStorage.length - 1; index >= 0; index -= 1 ) {
            const key = window.localStorage.key( index );
            if ( key?.startsWith( `${STORAGE_KEY_PREFIX}:` ) ) {
                window.localStorage.removeItem( key );
            }
        }
    } catch ( error ) {
        console.error( "Failed to clear all trade panel order types:", error );
    }
}

let lastSelectedOrderType: OrderTypeMemory | undefined;

export function clearOrderTypeMemory() {
    lastSelectedOrderType = undefined;
    removeOrderTypeMemory();
}

export function clearAllOrderTypeMemory() {
    lastSelectedOrderType = undefined;
    removeAllOrderTypeMemory();
}

export function getOrderTypeMemory(): OrderType | undefined {
    const userId = getCurrentUserId();
    if ( userId === undefined ) return undefined;

    if ( lastSelectedOrderType?.userId === userId ) {
        return lastSelectedOrderType.orderType;
    }

    const orderType = readOrderTypeMemory();
    lastSelectedOrderType = orderType ? { userId, orderType } : undefined;
    return orderType;
}

export function setOrderTypeMemory( orderType: OrderType ) {
    const userId = getCurrentUserId();
    if ( userId === undefined ) return;

    lastSelectedOrderType = { userId, orderType };
    writeOrderTypeMemory( orderType );
}

export function useOrderTypeMemory() {
    return {
        save: ( orderType: OrderType ) => {
            setOrderTypeMemory( orderType );
        },
        get: () => getOrderTypeMemory(),
    };
}
